import type { AttributionEvidenceType } from "@redotech/redo-attribution-common/attribution-settings";
import { PerformanceRow } from "@redotech/redo-marketing-analytics/types";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { ATTRIBUTION_QUERY_OPTIONS } from "@redotech/redo-merchant-app-common/marketing/attribution-query-options";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoMetricItem } from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import {
  RedoTable,
  RedoTableColumn,
  RedoTableLayout,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTableBadgeCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { RedoTablePagination } from "@redotech/redo-web/arbiter-components/tables/redo-table-pagination";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { memo, useContext, useMemo, useState } from "react";
import { analyticsDateRange } from "../analytics-date-range";
import {
  BotFilter,
  clickBasisExplanation,
  clickBasisLabel,
  getClickBotFilter,
} from "../filters/marketing-bot-filter";
import {
  ChannelFilter,
  getChannelsForFilter,
} from "../filters/marketing-channel-filter";
import { getEmptyStateMessage } from "./marketing-analytics-automation-performance";
import { MarketingEventNameCell } from "./marketing-event-name-cell";

export const MarketingAnalyticsCampaignPerformance = memo(
  function MarketingAnalyticsCampaignPerformance({
    dateFilter,
    attributionDays,
    channelFilter,
    attributionEvidence,
    botFilter,
  }: {
    dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
    attributionDays: number | undefined;
    channelFilter: ChannelFilter;
    attributionEvidence: AttributionEvidenceType[];
    botFilter: BotFilter;
  }) {
    const { formatCurrency } = useContext(CurrencyContext);

    const { startDate, endDate } = analyticsDateRange(dateFilter);

    const {
      data: campaignPerformanceResult,
      isLoading,
      error: failedToLoad,
    } = useRpcQuery(
      RedoMarketingRpcClientContext,
      "getCampaignPerformanceAnalytics",
      {
        startDate,
        endDate,
        attributionDays,
        channels: getChannelsForFilter(channelFilter),
        attributionEvidence,
        clickBotFilter: getClickBotFilter(botFilter),
      },
      ATTRIBUTION_QUERY_OPTIONS,
    );

    const formatRoiMultiplier = (value: number) => {
      const multiplier = value / 100 + 1;
      return `${multiplier.toFixed(1)}×`;
    };

    const calculatePercentageChange = (current: number, previous: number) => {
      if (previous === 0 && current === 0) {
        return 0;
      }
      if (previous === 0) {
        return null;
      }

      const change = ((current - previous) / previous) * 100;

      // Cap extremely large percentage changes for display purposes
      if (change > 999) {
        return 999;
      }
      if (change < -999) {
        return -999;
      }

      return change;
    };

    const totals = campaignPerformanceResult?.totals;
    const campaigns = useMemo(
      () =>
        ((campaignPerformanceResult?.campaigns || []) as PerformanceRow[])
          // Drops drafts but keeps a campaign that sent earlier and converted
          // in range; its revenue is in the totals above these rows.
          .filter((c) => isDef(c.SENT_DATE) || c.REVENUE > 0),
      [campaignPerformanceResult?.campaigns],
    );

    type CampaignRow = (typeof campaigns)[number];

    const clickBasis = clickBasisLabel(botFilter);
    const clickBasisNote = clickBasisExplanation(botFilter)?.join(" ");

    const tableColumns = useMemo((): RedoTableColumn<CampaignRow>[] => {
      return [
        {
          key: "name",
          widthMode: { type: "fill" },
          alignment: "left",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Campaign" }),
          renderNormalCell: ({ row }) => <MarketingEventNameCell row={row} />,
        },
        {
          key: "channel",
          widthMode: { type: "fixed", width: 100 },
          alignment: "center",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Channel" }),
          renderNormalCell: ({ row }) => {
            const channelColor =
              row.CHANNEL === "email" ? "blueLight" : "purple";
            const channelText = row.CHANNEL === "email" ? "Email" : "SMS";
            return (
              <RedoTableBadgeCell
                badges={[{ text: channelText, color: channelColor }]}
              />
            );
          },
        },
        {
          key: "ctr",
          widthMode: { type: "fixed", width: clickBasis ? 140 : 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({
              headerText: clickBasis ? `${clickBasis} CTR` : "CTR",
              helpIconTooltip: clickBasisNote,
            }),
          renderNormalCell: ({ row }) => {
            const ctr =
              row.TOTAL_SENDS > 0
                ? (row.TOTAL_CLICKS / row.TOTAL_SENDS) * 100
                : 0;
            return <RedoTableTextCell text={`${ctr.toFixed(1)}%`} />;
          },
        },
        {
          key: "cvr",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "CVR" }),
          renderNormalCell: ({ row }) => {
            const cvr =
              row.UNIQUE_RECIPIENTS > 0
                ? (row.TOTAL_ORDERS / row.UNIQUE_RECIPIENTS) * 100
                : 0;
            return <RedoTableTextCell text={`${cvr.toFixed(1)}%`} />;
          },
        },
        {
          key: "cost",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Spend" }),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={formatCurrency(row.SPEND)} />
          ),
        },
        {
          key: "roi",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "ROI" }),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={formatRoiMultiplier(row.ROI ?? 0)} />
          ),
        },
        {
          key: "revenue",
          widthMode: { type: "fixed", width: 160 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Attributed revenue" }),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={formatCurrency(row.REVENUE)} />
          ),
        },
      ];
    }, [formatCurrency, clickBasis, clickBasisNote]);

    const tableLayout: RedoTableLayout<CampaignRow> = useMemo(
      () => ({ columns: tableColumns, headerColor: "white", standalone: true }),
      [tableColumns],
    );

    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    const filteredCampaigns = useMemo(() => {
      if (!searchQuery) {
        return campaigns;
      }
      const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, "");
      const normalizedQuery = normalize(searchQuery);
      return campaigns.filter((c) =>
        normalize(c.NAME ?? c.KEY).includes(normalizedQuery),
      );
    }, [campaigns, searchQuery]);

    const totalPages = Math.max(
      1,
      Math.ceil(filteredCampaigns.length / pageSize),
    );
    const clampedPage = Math.min(currentPage, totalPages);
    const pageCampaigns = filteredCampaigns.slice(
      (clampedPage - 1) * pageSize,
      clampedPage * pageSize,
    );

    const revenueChange = totals
      ? calculatePercentageChange(
          totals.totalRevenue,
          totals.previousTotalRevenue,
        )
      : 0;
    const spendChange = totals
      ? calculatePercentageChange(totals.totalSpend, totals.previousTotalSpend)
      : 0;
    const roiChange = totals
      ? calculatePercentageChange(totals.totalROI, totals.previousTotalROI)
      : 0;
    const revenuePerRecipientChange = totals
      ? calculatePercentageChange(
          totals.totalRecipients > 0
            ? totals.totalRevenue / totals.totalRecipients
            : 0,
          totals.previousTotalRecipients > 0
            ? totals.previousTotalRevenue / totals.previousTotalRecipients
            : 0,
        )
      : 0;

    if (failedToLoad) {
      return (
        <Flex dir="column" gap="4xl" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Campaign Performance
          </Text>
          <Text textColor="error">Error loading campaign performance data</Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="xl" w="full">
        <Text fontSize="md" fontWeight="semibold">
          Campaign Performance
        </Text>
        <Flex dir="row" gap="4xl" w="full">
          <RedoMetricItem
            pending={isLoading}
            title="Campaign Revenue"
            value={formatCurrency(totals?.totalRevenue ?? 0)}
            {...(revenueChange !== null && {
              comparisonChange: revenueChange,
              comparisonText: "vs previous period",
              isGoodChange: revenueChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Campaign Spend"
            value={formatCurrency(totals?.totalSpend ?? 0)}
            {...(spendChange !== null && {
              comparisonChange: spendChange,
              comparisonText: "vs previous period",
              isGoodChange: spendChange <= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Campaign ROI"
            value={formatRoiMultiplier(totals?.totalROI ?? 0)}
            {...(roiChange !== null && {
              comparisonChange: roiChange,
              comparisonText: "vs previous period",
              isGoodChange: roiChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Revenue per recipient"
            value={
              totals?.totalRecipients && totals.totalRecipients > 0
                ? formatCurrency(totals.totalRevenue / totals.totalRecipients)
                : formatCurrency(0)
            }
            {...(revenuePerRecipientChange !== null && {
              comparisonChange: revenuePerRecipientChange,
              comparisonText: "vs previous period",
              isGoodChange: revenuePerRecipientChange >= 0,
            })}
          />
        </Flex>

        <Flex align="center" justify="space-between" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Campaigns
          </Text>
          <RedoTextInput
            placeholder="Search campaigns..."
            setValue={(value) => {
              setSearchQuery(value);
              setCurrentPage(1);
            }}
            size="sm"
            value={searchQuery}
          />
        </Flex>
        <Flex
          borderColor="primary"
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="none"
          w="full"
        >
          <RedoTable<CampaignRow>
            emptyStateContent={
              filteredCampaigns.length === 0 && !isLoading ? (
                <Flex align="center" justify="center" py="8xl">
                  <Text textColor="tertiary">
                    {searchQuery
                      ? `No campaigns matching "${searchQuery}"`
                      : getEmptyStateMessage(channelFilter, "campaign")}
                  </Text>
                </Flex>
              ) : undefined
            }
            layout={tableLayout}
            loadingStateContent={
              isLoading ? (
                <Flex align="center" justify="center" py="8xl">
                  <LoadingRedoAnimation />
                </Flex>
              ) : undefined
            }
            rowsOfData={isLoading ? [] : pageCampaigns}
            size="md"
          />
          <RedoTablePagination
            currentPage={clampedPage}
            knownRowCount={filteredCampaigns.length}
            pageSize={pageSize}
            pageSizes={[5, 10, 20, 50]}
            setCurrentPage={setCurrentPage}
            setPageSize={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            size="sm"
          />
        </Flex>
      </Flex>
    );
  },
);
